<template>
  <v-dialog v-model="showCudaDriverMapDialog" max-width="700" @click:outside="closeDialog">
    <v-card>
      <v-card-title>
        <v-row align="center">
          <v-col cols="8">
            <span class="text-h6">CUDA Driver Map</span>
            <v-chip small class="ml-2" color="grey lighten-2">{{ entries.length }} entries</v-chip>
          </v-col>
          <v-col cols="4" class="text-right">
            <v-btn icon small color="primary" @click="fetchEntries" :loading="loading" class="mr-1">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-btn icon @click="closeDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text>
        <!-- Add New Entry -->
        <v-row dense>
          <v-col cols="12" class="pb-0">
            <v-expand-transition>
              <v-card v-if="showAddForm" outlined class="mb-3 pa-3">
                <v-row dense align="center">
                  <v-col cols="4">
                    <v-text-field
                      v-model="newEntry.cuda_major"
                      label="CUDA Major Version"
                      placeholder="e.g. 12"
                      dense
                      outlined
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      v-model="newEntry.min_driver"
                      label="Min Driver Version"
                      placeholder="e.g. 525.00"
                      dense
                      outlined
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="4" class="d-flex">
                    <v-btn small color="primary" @click="createEntry" :loading="saving" class="mr-1">
                      <v-icon small left>mdi-check</v-icon>Save
                    </v-btn>
                    <v-btn small outlined @click="cancelAdd">Cancel</v-btn>
                  </v-col>
                </v-row>
              </v-card>
            </v-expand-transition>
          </v-col>
        </v-row>

        <!-- JSON Import -->
        <v-row dense>
          <v-col cols="12" class="pb-0">
            <v-expand-transition>
              <v-card v-if="showJsonImport" outlined class="mb-3 pa-3">
                <v-textarea
                  v-model="jsonImportText"
                  label="Paste JSON (object or array format)"
                  placeholder='{"13": "580.00", "12": "525.00"} or [{"cuda_major": "13", "min_driver": "580.00"}]'
                  dense
                  outlined
                  rows="4"
                  hide-details
                  class="mb-2"
                  style="font-family: monospace; font-size: 13px;"
                ></v-textarea>
                <div class="d-flex">
                  <v-btn small color="primary" @click="importJson" :loading="saving" class="mr-1">
                    <v-icon small left>mdi-import</v-icon>Import
                  </v-btn>
                  <v-btn small outlined @click="showJsonImport = false; jsonImportText = ''">Cancel</v-btn>
                </div>
              </v-card>
            </v-expand-transition>
          </v-col>
        </v-row>

        <!-- Action Buttons Row -->
        <v-row dense class="mb-1">
          <v-col cols="12" class="d-flex align-center">
            <v-btn small color="primary" outlined @click="toggleAdd" v-if="!showAddForm">
              <v-icon small left>mdi-plus</v-icon>Add Entry
            </v-btn>
            <v-btn small outlined class="ml-2" @click="showJsonImport = !showJsonImport" v-if="!showJsonImport">
              <v-icon small left>mdi-code-json</v-icon>JSON Import
            </v-btn>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              clearable
              dense
              hide-details
              label="Search"
              outlined
              prepend-inner-icon="mdi-magnify"
              style="max-width: 220px;"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Data Table -->
        <v-data-table
          :headers="headers"
          :items="entries"
          :search="search"
          dense
          class="elevation-1"
          no-data-text="No CUDA driver map entries"
          :items-per-page="10"
          sort-by="cuda_major"
          sort-desc
        >
          <template v-slot:item.cuda_major="{ item }">
            <div v-if="editingEntry && editingEntry.id === item.id">
              <v-text-field
                v-model="editingEntry.cuda_major"
                dense
                hide-details
                class="pa-0"
                style="max-width: 100px;"
              ></v-text-field>
            </div>
            <div v-else>
              <v-chip small label color="blue" dark>CUDA {{ item.cuda_major }}.x</v-chip>
            </div>
          </template>

          <template v-slot:item.min_driver="{ item }">
            <div v-if="editingEntry && editingEntry.id === item.id">
              <v-text-field
                v-model="editingEntry.min_driver"
                dense
                hide-details
                class="pa-0"
                style="max-width: 140px;"
              ></v-text-field>
            </div>
            <div v-else>
              <code>{{ item.min_driver }}</code>
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn v-if="!editingEntry || editingEntry.id !== item.id" icon small @click="startEdit(item)">
              <v-icon small color="primary">mdi-pencil</v-icon>
            </v-btn>
            <v-btn v-if="editingEntry && editingEntry.id === item.id" icon small @click="saveEdit" :loading="saving">
              <v-icon small color="success">mdi-check</v-icon>
            </v-btn>
            <v-btn v-if="editingEntry && editingEntry.id === item.id" icon small @click="cancelEdit">
              <v-icon small color="grey">mdi-close</v-icon>
            </v-btn>
            <v-btn icon small @click="deleteEntry(item)">
              <v-icon small color="error">mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import Swal from 'sweetalert2'
import { apiService } from '../../service/api.service'

export default {
  name: 'CudaDriverMapDialog',
  data () {
    return {
      entries: [],
      search: '',
      loading: false,
      saving: false,
      showAddForm: false,
      showJsonImport: false,
      jsonImportText: '',
      editingEntry: null,
      newEntry: {
        cuda_major: '',
        min_driver: ''
      },
      headers: [
        { text: 'CUDA Major', value: 'cuda_major', align: 'start', sortable: true },
        { text: 'Min Driver Version', value: 'min_driver', align: 'start', sortable: true },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false, width: '140px' }
      ]
    }
  },
  props: {
    showCudaDriverMapDialog: Boolean
  },
  watch: {
    showCudaDriverMapDialog (val) {
      if (val) {
        this.fetchEntries()
      }
    }
  },
  methods: {
    closeDialog () {
      this.editingEntry = null
      this.showAddForm = false
      this.showJsonImport = false
      this.$emit('close-cuda-driver-map')
    },
    async fetchEntries () {
      this.loading = true
      try {
        const response = await apiService.get('admin/cuda-driver-map')
        if (response.data && response.data.data) {
          this.entries = response.data.data
        }
      } catch (error) {
        console.error('Failed to fetch CUDA driver map:', error)
        Swal.fire('Error', 'Failed to fetch CUDA driver map.', 'error')
      } finally {
        this.loading = false
      }
    },
    toggleAdd () {
      this.showAddForm = true
      this.newEntry = { cuda_major: '', min_driver: '' }
    },
    cancelAdd () {
      this.showAddForm = false
      this.newEntry = { cuda_major: '', min_driver: '' }
    },
    async createEntry () {
      if (!this.newEntry.cuda_major || !this.newEntry.min_driver) {
        Swal.fire('Warning', 'Both CUDA Major and Min Driver are required.', 'warning')
        return
      }
      this.saving = true
      try {
        await apiService.post('admin/cuda-driver-map', {
          cuda_major: this.newEntry.cuda_major,
          min_driver: this.newEntry.min_driver
        })
        Swal.fire('Success', 'CUDA driver map entry created.', 'success')
        this.cancelAdd()
        await this.fetchEntries()
      } catch (error) {
        const msg = error.response?.data?.message || 'Failed to create entry.'
        Swal.fire('Error', msg, 'error')
      } finally {
        this.saving = false
      }
    },
    startEdit (item) {
      this.editingEntry = { ...item }
    },
    cancelEdit () {
      this.editingEntry = null
    },
    async saveEdit () {
      if (!this.editingEntry.cuda_major || !this.editingEntry.min_driver) {
        Swal.fire('Warning', 'Both fields are required.', 'warning')
        return
      }
      this.saving = true
      try {
        await apiService.put(`admin/cuda-driver-map/${this.editingEntry.id}`, {
          cuda_major: this.editingEntry.cuda_major,
          min_driver: this.editingEntry.min_driver
        })
        Swal.fire('Success', 'Entry updated.', 'success')
        this.editingEntry = null
        await this.fetchEntries()
      } catch (error) {
        const msg = error.response?.data?.message || 'Failed to update entry.'
        Swal.fire('Error', msg, 'error')
      } finally {
        this.saving = false
      }
    },
    async deleteEntry (item) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `Delete CUDA ${item.cuda_major} → ${item.min_driver}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!'
      })
      if (result.isConfirmed) {
        try {
          await apiService.delete(`admin/cuda-driver-map/${item.id}`)
          Swal.fire('Deleted!', 'Entry has been deleted.', 'success')
          await this.fetchEntries()
        } catch (error) {
          Swal.fire('Error', 'Failed to delete entry.', 'error')
        }
      }
    },
    async importJson () {
      if (!this.jsonImportText.trim()) {
        Swal.fire('Warning', 'Please paste JSON content.', 'warning')
        return
      }
      let parsed
      try {
        parsed = JSON.parse(this.jsonImportText.trim())
      } catch (e) {
        Swal.fire('Error', 'Invalid JSON format.', 'error')
        return
      }

      // Support both object {"13": "580.00"} and array [{cuda_major, min_driver}] formats
      let entriesToImport = []
      if (Array.isArray(parsed)) {
        entriesToImport = parsed.map(e => ({
          cuda_major: String(e.cuda_major),
          min_driver: String(e.min_driver)
        }))
      } else if (typeof parsed === 'object') {
        for (const [key, value] of Object.entries(parsed)) {
          entriesToImport.push({ cuda_major: String(key), min_driver: String(value) })
        }
      } else {
        Swal.fire('Error', 'Expected a JSON object or array.', 'error')
        return
      }

      if (entriesToImport.length === 0) {
        Swal.fire('Warning', 'No entries found in JSON.', 'warning')
        return
      }

      this.saving = true
      let successCount = 0
      let failCount = 0
      for (const entry of entriesToImport) {
        try {
          await apiService.post('admin/cuda-driver-map', entry)
          successCount++
        } catch (error) {
          // If already exists, try update by finding the existing entry
          const existing = this.entries.find(e => e.cuda_major === entry.cuda_major)
          if (existing) {
            try {
              await apiService.put(`admin/cuda-driver-map/${existing.id}`, entry)
              successCount++
            } catch (updateError) {
              failCount++
            }
          } else {
            failCount++
          }
        }
      }
      this.saving = false
      this.showJsonImport = false
      this.jsonImportText = ''
      await this.fetchEntries()
      Swal.fire('Import Complete', `${successCount} succeeded, ${failCount} failed.`, successCount > 0 ? 'success' : 'error')
    }
  },
  mounted () {
    if (this.showCudaDriverMapDialog) {
      this.fetchEntries()
    }
  }
}
</script>
